/**
 * Time-phased financial I/O — funding envelopes + launch cost plans (Planisware FIN).
 */

import { getSupabaseClient } from './supabaseClient';
import { generateTimeBuckets } from './planningEngine';
import { runTimePhasedFinancialEngine } from './timePhasedFinancialEngine';
import type { TimePhasedPlacement } from './timePhasedPlanning.types';
import type {
  BucketMoneyUtilization,
  FundingAffordability,
  FundingEnvelope,
  FundingPeriod,
  PriorityCostPlan,
  TimePhasedFinancialOutput,
} from './timePhasedFinancial.types';

function addDaysIso(isoDate: string, days: number): string {
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function toEnvelope(row: Record<string, unknown>, periods: FundingPeriod[]): FundingEnvelope {
  return {
    id: row.id as string,
    projectId: row.project_id as string,
    scenarioId: row.scenario_id as string,
    name: row.name as string,
    currency: (row.currency as string) ?? 'USD',
    owningOrgUnitId: (row.owning_org_unit_id as string) ?? undefined,
    strategicCategory: (row.strategic_category as string) ?? undefined,
    fundingSource: (row.funding_source as string) ?? undefined,
    notes: (row.notes as string) ?? undefined,
    periods,
  };
}

function toCostPlan(row: Record<string, unknown>): PriorityCostPlan {
  return {
    id: row.id as string,
    projectId: row.project_id as string,
    priorityId: row.priority_id as string,
    name: (row.name as string) ?? undefined,
    currency: (row.currency as string) ?? 'USD',
    costCategory: (row.cost_category as PriorityCostPlan['costCategory']) ?? 'other',
    capexOpex: (row.capex_opex as PriorityCostPlan['capexOpex']) ?? 'unspecified',
    estimateAmount: row.estimate_amount != null ? Number(row.estimate_amount) : null,
    approvedAmount: row.approved_amount != null ? Number(row.approved_amount) : null,
    forecastAmount: Number(row.forecast_amount ?? 0),
    commitmentAmount: row.commitment_amount != null ? Number(row.commitment_amount) : null,
    actualAmount: row.actual_amount != null ? Number(row.actual_amount) : null,
    phasingMode: (row.phasing_mode as PriorityCostPlan['phasingMode']) ?? 'launch_relative',
    offsetStartWeeks: Number(row.offset_start_weeks ?? -4),
    offsetEndWeeks: Number(row.offset_end_weeks ?? 0),
    laborHours: row.labor_hours != null ? Number(row.labor_hours) : null,
    hourlyRate: row.hourly_rate != null ? Number(row.hourly_rate) : null,
    notes: (row.notes as string) ?? undefined,
  };
}

export async function getFundingEnvelopes(scenarioId: string): Promise<FundingEnvelope[]> {
  const sb = getSupabaseClient();
  const { data: envs, error } = await sb
    .from('planning_funding_envelopes')
    .select('*')
    .eq('scenario_id', scenarioId)
    .order('name');
  if (error) throw error;
  if (!envs?.length) return [];

  const ids = envs.map((e) => e.id as string);
  const { data: periods, error: pErr } = await sb
    .from('planning_funding_periods')
    .select('*')
    .in('envelope_id', ids)
    .order('bucket_start');
  if (pErr) throw pErr;

  const byEnv = new Map<string, FundingPeriod[]>();
  for (const p of periods ?? []) {
    const list = byEnv.get(p.envelope_id as string) ?? [];
    list.push({
      id: p.id as string,
      envelopeId: p.envelope_id as string,
      bucketStart: p.bucket_start as string,
      bucketEnd: p.bucket_end as string,
      approvedAmount: Number(p.approved_amount ?? 0),
      forecastAmount: p.forecast_amount != null ? Number(p.forecast_amount) : null,
    });
    byEnv.set(p.envelope_id as string, list);
  }

  return envs.map((e) => toEnvelope(e as Record<string, unknown>, byEnv.get(e.id as string) ?? []));
}

export async function upsertFundingEnvelope(input: {
  id?: string;
  projectId: string;
  scenarioId: string;
  name: string;
  currency?: string;
  strategicCategory?: string;
  fundingSource?: string;
  notes?: string;
}): Promise<string> {
  const sb = getSupabaseClient();
  const row = {
    id: input.id,
    project_id: input.projectId,
    scenario_id: input.scenarioId,
    name: input.name,
    currency: input.currency ?? 'USD',
    strategic_category: input.strategicCategory ?? null,
    funding_source: input.fundingSource ?? null,
    notes: input.notes ?? null,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await sb
    .from('planning_funding_envelopes')
    .upsert(row)
    .select('id')
    .single();
  if (error) throw error;
  return data.id as string;
}

export async function replaceFundingPeriods(
  envelopeId: string,
  periods: Omit<FundingPeriod, 'id' | 'envelopeId'>[],
): Promise<void> {
  const sb = getSupabaseClient();
  await sb.from('planning_funding_periods').delete().eq('envelope_id', envelopeId);
  if (!periods.length) return;
  const { error } = await sb.from('planning_funding_periods').insert(
    periods.map((p) => ({
      envelope_id: envelopeId,
      bucket_start: p.bucketStart,
      bucket_end: p.bucketEnd,
      approved_amount: p.approvedAmount,
      forecast_amount: p.forecastAmount ?? null,
    })),
  );
  if (error) throw error;
}

/**
 * Seed a flat weekly approved envelope across the horizon (FIN-001).
 *
 * Ensure-semantics, strictly: seeds only when the scenario has no envelope or
 * the envelope has no periods yet. Saved period amounts are durable user state
 * (§2.1) — this must never flatten them. It used to replace all periods with
 * the flat default on every call, so each demo seed / Launch Calendar
 * ensure-run silently wiped weekly funding edited on Funding & Costs.
 */
export async function ensureHorizonFundingEnvelope(
  projectId: string,
  scenarioId: string,
  horizonStart: string,
  horizonEnd: string,
  weeklyApproved: number,
  name = 'Portfolio funding',
): Promise<FundingEnvelope> {
  const existing = await getFundingEnvelopes(scenarioId);
  if (existing[0] && existing[0].periods.length > 0) {
    return existing[0];
  }
  let envelopeId = existing[0]?.id;
  if (!envelopeId) {
    envelopeId = await upsertFundingEnvelope({
      projectId,
      scenarioId,
      name,
      currency: 'USD',
      fundingSource: 'operating',
    });
  }
  const buckets = generateTimeBuckets(horizonStart, horizonEnd, 'week');
  await replaceFundingPeriods(
    envelopeId,
    buckets.map((b) => ({
      bucketStart: b.time_bucket_start,
      bucketEnd: b.time_bucket_end,
      approvedAmount: weeklyApproved,
    })),
  );
  const refreshed = await getFundingEnvelopes(scenarioId);
  return refreshed.find((e) => e.id === envelopeId) ?? refreshed[0];
}

export async function getCostPlansForScenario(
  projectId: string,
  scenarioId: string,
): Promise<PriorityCostPlan[]> {
  const sb = getSupabaseClient();
  const { data: priorities, error: pErr } = await sb
    .from('planning_priorities')
    .select('id')
    .eq('project_id', projectId)
    .or(`scenario_id.eq.${scenarioId},scenario_id.is.null`);
  if (pErr) throw pErr;
  const ids = (priorities ?? []).map((p) => p.id as string);
  if (!ids.length) return [];

  const { data, error } = await sb
    .from('planning_priority_cost_plans')
    .select('*')
    .in('priority_id', ids);
  if (error) throw error;
  return (data ?? []).map((r) => toCostPlan(r as Record<string, unknown>));
}

export async function upsertCostPlan(
  plan: Omit<PriorityCostPlan, 'id'> & { id?: string },
): Promise<PriorityCostPlan> {
  const sb = getSupabaseClient();
  const row = {
    id: plan.id,
    project_id: plan.projectId,
    priority_id: plan.priorityId,
    name: plan.name ?? null,
    currency: plan.currency,
    cost_category: plan.costCategory,
    capex_opex: plan.capexOpex,
    estimate_amount: plan.estimateAmount ?? null,
    approved_amount: plan.approvedAmount ?? null,
    forecast_amount: plan.forecastAmount,
    commitment_amount: plan.commitmentAmount ?? null,
    actual_amount: plan.actualAmount ?? null,
    phasing_mode: plan.phasingMode,
    offset_start_weeks: plan.offsetStartWeeks,
    offset_end_weeks: plan.offsetEndWeeks,
    labor_hours: plan.laborHours ?? null,
    hourly_rate: plan.hourlyRate ?? null,
    notes: plan.notes ?? null,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await sb
    .from('planning_priority_cost_plans')
    .upsert(row)
    .select('*')
    .single();
  if (error) throw error;
  return toCostPlan(data as Record<string, unknown>);
}

export async function deleteCostPlan(id: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_priority_cost_plans')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/** Set every period on an envelope to the same weekly approved amount (FIN-001 edit). */
export async function setEnvelopeWeeklyApproved(
  envelopeId: string,
  periods: Omit<FundingPeriod, 'id' | 'envelopeId'>[],
  weeklyApproved: number,
): Promise<void> {
  await replaceFundingPeriods(
    envelopeId,
    periods.map((p) => ({
      bucketStart: p.bucketStart,
      bucketEnd: p.bucketEnd,
      approvedAmount: weeklyApproved,
      forecastAmount: p.forecastAmount,
    })),
  );
}

export async function persistFinancialSnapshot(
  scenarioId: string,
  financial: TimePhasedFinancialOutput,
): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('planning_scenarios')
    .update({
      time_phased_financial_utilization: {
        algorithmVersion: 'time_phased_fin_v1',
        moneyUtilization: financial.moneyUtilization,
        affordability: financial.affordability,
        ranAt: new Date().toISOString(),
      },
    })
    .eq('id', scenarioId);
  if (error) throw error;
}

export async function getLatestFinancialUtilization(scenarioId: string): Promise<{
  moneyUtilization: BucketMoneyUtilization[];
  affordability: FundingAffordability | null;
}> {
  const { data, error } = await getSupabaseClient()
    .from('planning_scenarios')
    .select('time_phased_financial_utilization')
    .eq('id', scenarioId)
    .single();
  if (error) throw error;
  const payload = data?.time_phased_financial_utilization as
    | {
        moneyUtilization?: BucketMoneyUtilization[];
        affordability?: FundingAffordability;
      }
    | null
    | undefined;
  return {
    moneyUtilization: payload?.moneyUtilization ?? [],
    affordability: payload?.affordability ?? null,
  };
}

export async function runFinancialForPlacements(
  projectId: string,
  scenarioId: string,
  horizonStart: string,
  horizonEnd: string,
  placements: TimePhasedPlacement[],
): Promise<TimePhasedFinancialOutput> {
  const [envelopes, costPlans] = await Promise.all([
    getFundingEnvelopes(scenarioId),
    getCostPlansForScenario(projectId, scenarioId),
  ]);
  const financial = runTimePhasedFinancialEngine({
    horizonStart,
    horizonEnd,
    placements,
    costPlans,
    envelopes,
  });
  await persistFinancialSnapshot(scenarioId, financial);
  return financial;
}

/** Copy funding envelopes + periods onto a hypothesis scenario (counterfactuals). */
export async function copyFundingEnvelopesToScenario(
  fromScenarioId: string,
  toScenarioId: string,
  projectId: string,
): Promise<void> {
  const envelopes = await getFundingEnvelopes(fromScenarioId);
  for (const env of envelopes) {
    const newId = await upsertFundingEnvelope({
      projectId,
      scenarioId: toScenarioId,
      name: env.name,
      currency: env.currency,
      strategicCategory: env.strategicCategory,
      fundingSource: env.fundingSource,
      notes: env.notes,
    });
    await replaceFundingPeriods(
      newId,
      env.periods.map((p) => ({
        bucketStart: p.bucketStart,
        bucketEnd: p.bucketEnd,
        approvedAmount: p.approvedAmount,
        forecastAmount: p.forecastAmount,
      })),
    );
  }
}

/** Attach demo cost plans + tight envelope for Keith / sample seeds. */
export async function seedDemoFinancialLayer(input: {
  projectId: string;
  scenarioId: string;
  horizonStart: string;
  horizonEnd: string;
  prioritySpecs: { priorityId: string; forecastAmount: number; name?: string }[];
  weeklyApproved: number;
  envelopeName?: string;
}): Promise<void> {
  await ensureHorizonFundingEnvelope(
    input.projectId,
    input.scenarioId,
    input.horizonStart,
    input.horizonEnd,
    input.weeklyApproved,
    input.envelopeName ?? 'Portfolio funding',
  );

  for (const spec of input.prioritySpecs) {
    const existing = await getCostPlansForScenario(input.projectId, input.scenarioId);
    const has = existing.some((c) => c.priorityId === spec.priorityId);
    if (has) continue;
    await upsertCostPlan({
      projectId: input.projectId,
      priorityId: spec.priorityId,
      name: spec.name ?? 'Launch cost',
      currency: 'USD',
      costCategory: 'other',
      capexOpex: 'opex',
      estimateAmount: spec.forecastAmount,
      approvedAmount: spec.forecastAmount * 0.9,
      forecastAmount: spec.forecastAmount,
      commitmentAmount: null,
      actualAmount: null,
      phasingMode: 'launch_relative',
      offsetStartWeeks: -4,
      offsetEndWeeks: 0,
      notes: 'Demo financial layer — forecast moves with launch placement',
    });
  }
}

/**
 * Make funding visible for an existing calendar scenario.
 * Seeds envelope + per-launch cost plans when missing, then runs the FIN engine
 * so Launch Calendar always has an affordability snapshot (not demo-only).
 */
export async function ensureScenarioFinancialLayer(
  projectId: string,
  scenarioId: string,
  placements: TimePhasedPlacement[],
): Promise<TimePhasedFinancialOutput | null> {
  if (placements.length === 0) return null;

  const sb = getSupabaseClient();
  const { data: scenarioRow, error: scenarioError } = await sb
    .from('planning_scenarios')
    .select('horizon_start_date, horizon_end_date, planning_weeks')
    .eq('id', scenarioId)
    .single();
  if (scenarioError) throw scenarioError;

  const starts = placements
    .map((p) => p.feasibleStart)
    .filter((d): d is string => Boolean(d))
    .sort();
  const ends = placements
    .map((p) => p.feasibleEnd ?? p.feasibleStart)
    .filter((d): d is string => Boolean(d))
    .sort();

  const horizonStart =
    (scenarioRow?.horizon_start_date as string | null) ??
    starts[0] ??
    new Date().toISOString().slice(0, 10);
  const horizonEnd =
    (scenarioRow?.horizon_end_date as string | null) ??
    ends[ends.length - 1] ??
    addDaysIso(horizonStart, ((scenarioRow?.planning_weeks as number) || 14) * 7);

  const [envelopes, costPlans] = await Promise.all([
    getFundingEnvelopes(scenarioId),
    getCostPlansForScenario(projectId, scenarioId),
  ]);

  if (envelopes.length === 0 || costPlans.length === 0) {
    const anchored = placements.filter((p) => p.feasibleStart);
    const specs = (anchored.length > 0 ? anchored : placements).slice(0, 48).map((p, i) => ({
      priorityId: p.priorityId,
      forecastAmount: 28000 + (i % 6) * 9000,
      name: `${p.priorityName} launch cost`,
    }));
    const weekCount = Math.max(
      1,
      generateTimeBuckets(horizonStart, horizonEnd, 'week').length,
    );
    const totalDemand = specs.reduce((s, x) => s + x.forecastAmount, 0);
    // Tight envelope so $ heat is visible next to hours (same pattern as Keith demo).
    const weeklyApproved = Math.max(8000, Math.round((totalDemand / weekCount) * 0.55));

    await seedDemoFinancialLayer({
      projectId,
      scenarioId,
      horizonStart,
      horizonEnd,
      prioritySpecs: specs,
      weeklyApproved,
      envelopeName: 'Portfolio funding',
    });
  }

  return runFinancialForPlacements(
    projectId,
    scenarioId,
    horizonStart,
    horizonEnd,
    placements,
  );
}

export { addDaysIso };
